/**
 * The SOC lane (1.2.3–1.2.4): assessment capacity as its own strip. Per day,
 * three numbers and no fourth — **supply**, **Booked**, **open** — the same
 * numbers the referral panel reads for its 48h check.
 *
 * The standing SOC-block idea (the lane's old reserved and flexible counts) is
 * deliberately not drawn: nothing on this screen should imply a branch must keep
 * blocks aside for admissions. `selectSocLane` still computes them; see the
 * comment on `engine/socLane.ts`.
 */
import { useMemo } from "react";
import { Box, Paper, Stack, Tooltip, Typography } from "@mui/material";
import { addDaysToKey, dateKeyStart, formatInBranchZone, isoWeekday, weekStartKey } from "@caredna-marlin/utils/homeHealthDemo/engine/clock";
import { selectSocLane, type SocLaneDay } from "../../engine/socLane";
import { useDemoState } from "../../store/useDemoStore";

export function SocLane({ weekStart, spanWeeks = 2 }: { weekStart?: string; spanWeeks?: 2 | 4 }) {
  const state = useDemoState();
  const tz = state.config.branch.timeZone;
  const start = weekStart ?? weekStartKey(state.meta.demoToday);
  const days = useMemo(() => selectSocLane(state, start, addDaysToKey(start, spanWeeks * 7 - 1)), [state, start, spanWeeks]);
  const name = (h: string) => state.roster[h]?.displayName ?? h;

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
        <Typography variant="subtitle1" fontWeight={600}>
          SOC lane
        </Typography>
      </Stack>
      <Box sx={{ overflowX: "auto" }}>
        <Stack direction="row" spacing={0.5} sx={{ minWidth: 900 }}>
          {days.map((d) => (
            <LaneDay key={d.dateKey} day={d} today={d.dateKey === state.meta.demoToday} label={formatInBranchZone(dateKeyStart(d.dateKey, tz), tz, spanWeeks === 2 ? "EEE d" : "EEEEE d")} weekend={isoWeekday(d.dateKey) >= 6} name={name} />
          ))}
        </Stack>
      </Box>
      <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap sx={{ mt: 1, fontSize: 11.5, color: "text.secondary" }}>
        <Box component="span"><Box component="i" sx={{ display: "inline-block", width: 10, height: 10, bgcolor: "#5b7fa6", mr: 0.5, verticalAlign: -1 }} />Booked</Box>
        <Box component="span"><Box component="i" sx={{ display: "inline-block", width: 10, height: 10, bgcolor: "#fff", border: "1px solid #bbb", mr: 0.5, verticalAlign: -1 }} />open</Box>
        <Box component="span">pending = in ramp</Box>
      </Stack>
    </Paper>
  );
}

function LaneDay({ day, today, label, weekend, name }: { day: SocLaneDay; today: boolean; label: string; weekend: boolean; name: (h: string) => string }) {
  const slots = Math.max(day.slotsAvailable, day.booked, 0);
  const tip = [
    `supply ${day.slotsAvailable} SOC-capable slot${day.slotsAvailable === 1 ? "" : "s"}`,
    `Booked ${day.booked} (${day.bookedHolds} hold${day.bookedHolds === 1 ? "" : "s"}, ${day.bookedAssessments} assessment${day.bookedAssessments === 1 ? "" : "s"})`,
    `open ${day.open}`,
    day.supplyHandles.length ? `supply: ${day.supplyHandles.map(name).join(", ")}` : "no SOC-capable clinician on shift",
    day.pendingHandles.length ? `pending (ramp): ${day.pendingHandles.map(name).join(", ")}` : "",
  ]
    .filter(Boolean)
    .join("\n");
  return (
    <Tooltip title={<Box sx={{ whiteSpace: "pre-line" }}>{tip}</Box>}>
      <Box sx={{ flex: 1, minWidth: 56, border: "1px solid #e0e0e0", borderRadius: 1, p: 0.5, bgcolor: today ? "#fff4d6" : weekend ? "#fafafa" : "#fff" }}>
        <Typography sx={{ fontSize: 10.5, fontWeight: 600, textAlign: "center" }}>{label}</Typography>
        <Stack direction="row" spacing={0.25} justifyContent="center" flexWrap="wrap" useFlexGap sx={{ my: 0.5, minHeight: 14 }}>
          {Array.from({ length: slots }).map((_, i) => {
            const booked = i < day.booked;
            return <Box key={i} sx={{ width: 9, height: 12, borderRadius: 0.5, bgcolor: booked ? "#5b7fa6" : "#fff", border: `1px solid ${booked ? "#5b7fa6" : "#bbb"}` }} />;
          })}
          {slots === 0 && <Typography sx={{ fontSize: 10, color: "text.disabled" }}>—</Typography>}
        </Stack>
        <Typography sx={{ fontSize: 10.5, textAlign: "center", color: day.open < 0 ? "#a63d3d" : day.open === 0 ? "#b26a00" : "text.secondary" }}>
          {day.open < 0 ? `${day.open}` : `${day.open} open`}
          {day.pendingHandles.length ? ` · ${day.pendingHandles.length} pending` : ""}
        </Typography>
      </Box>
    </Tooltip>
  );
}
